import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { MOSOption, BiologicalSex } from '../types';
import {
  createSession as apiCreateSession,
  updateSession as apiUpdateSession,
  uploadImagesToR2,
} from '../services/session';

interface SessionState {
  sessionId: string | null;
  sessionCode: string | null;
  status: 'idle' | 'creating' | 'active' | 'processing' | 'completed' | 'error';
  error: string | null;
}

interface SessionContextType {
  session: SessionState;
  createSession: () => Promise<boolean>;
  updateSession: (data: {
    lastName?: string;
    sex?: BiologicalSex;
    selectedMOS?: MOSOption;
    originalPhoto?: string;
    status?: string;
  }) => Promise<boolean>;
  uploadImages: (portraitUrl: string, fieldUrl: string, mosId?: string) => Promise<{
    success: boolean;
    portraitUrl?: string;
    fieldUrl?: string;
  }>;
  getShareUrl: () => string;
  resetSession: () => void;
}

const SessionContext = createContext<SessionContextType | null>(null);

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<SessionState>({
    sessionId: null,
    sessionCode: null,
    status: 'idle',
    error: null,
  });

  // Store the createSession promise so other methods can wait for it
  const createPromiseRef = useRef<Promise<string | null> | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const createSession = useCallback(async (): Promise<boolean> => {
    setSession(prev => ({ ...prev, status: 'creating', error: null }));

    const promise = (async () => {
      try {
        const response = await apiCreateSession();

        if (response.success && response.sessionId && response.sessionCode) {
          sessionIdRef.current = response.sessionId;
          setSession({
            sessionId: response.sessionId,
            sessionCode: response.sessionCode,
            status: 'active',
            error: null,
          });
          return response.sessionId;
        } else {
          setSession(prev => ({
            ...prev,
            status: 'error',
            error: response.error || 'Failed to create session',
          }));
          return null;
        }
      } catch (error) {
        setSession(prev => ({
          ...prev,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
        }));
        return null;
      }
    })();

    createPromiseRef.current = promise;
    const result = await promise;
    return result !== null;
  }, []);

  // Helper to get session ID, waiting for creation if in progress
  const getSessionId = useCallback(async (): Promise<string | null> => {
    if (sessionIdRef.current) return sessionIdRef.current;
    if (createPromiseRef.current) {
      return await createPromiseRef.current;
    }
    return null;
  }, []);

  const updateSession = useCallback(async (data: {
    lastName?: string;
    sex?: BiologicalSex;
    selectedMOS?: MOSOption;
    originalPhoto?: string;
    status?: string;
  }): Promise<boolean> => {
    const sessionId = await getSessionId();
    if (!sessionId) {
      console.warn('No session ID available for update');
      return false;
    }

    try {
      const response = await apiUpdateSession(sessionId, data);
      return response.success;
    } catch (error) {
      console.error('Session update failed:', error);
      return false;
    }
  }, [getSessionId]);

  const uploadImages = useCallback(async (
    portraitUrl: string,
    fieldUrl: string,
    mosId?: string
  ): Promise<{ success: boolean; portraitUrl?: string; fieldUrl?: string }> => {
    const sessionId = await getSessionId();
    if (!sessionId) {
      console.warn('No session ID available for upload');
      return { success: false };
    }

    setSession(prev => ({ ...prev, status: 'processing' }));

    try {
      const response = await uploadImagesToR2(sessionId, portraitUrl, fieldUrl, mosId);

      if (response.success) {
        setSession(prev => ({ ...prev, status: 'completed' }));
        return {
          success: true,
          portraitUrl: response.portraitUrl,
          fieldUrl: response.fieldUrl,
        };
      } else {
        // Even if R2 upload fails, we can still use the original URLs
        setSession(prev => ({ ...prev, status: 'completed' }));
        return {
          success: false,
          portraitUrl,
          fieldUrl,
        };
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      setSession(prev => ({ ...prev, status: 'completed' }));
      return {
        success: false,
        portraitUrl,
        fieldUrl,
      };
    }
  }, [getSessionId]);

  const getShareUrl = useCallback((): string => {
    if (!session.sessionId) {
      return '';
    }
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/result?id=${session.sessionId}`;
  }, [session.sessionId]);

  const resetSession = useCallback(() => {
    sessionIdRef.current = null;
    createPromiseRef.current = null;
    setSession({
      sessionId: null,
      sessionCode: null,
      status: 'idle',
      error: null,
    });
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session,
        createSession,
        updateSession,
        uploadImages,
        getShareUrl,
        resetSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
